import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { CURRENCIES, type Currency, type LedgerAccount } from "../../entities/ledger/types";
import { humanError } from "../../shared/api/client";
import { DateTimeField } from "../../shared/ui/DateTimeField";
import { Select } from "../../shared/ui/Select";
import { localDateTimeValue } from "../../shared/lib/format";
import { currencyName, openAccount } from "./api";
import { openAccountSchema, type OpenAccountForm as OpenAccountValues } from "./model";

interface OpenAccountFormProps {
  bookID: string;
  currency: Currency;
  onOpened: (account: LedgerAccount) => void;
  onCancel?: () => void;
}

const currencies: Currency[] = CURRENCIES;

function defaults(currency: Currency): OpenAccountValues {
  return { name: "", currency, reference: "", openingAmount: "", effectiveAt: localDateTimeValue(), manualRate: "" };
}

export function OpenAccountForm({ bookID, currency, onOpened, onCancel }: OpenAccountFormProps) {
  const queryClient = useQueryClient();
  const form = useForm<OpenAccountValues>({ resolver: zodResolver(openAccountSchema), defaultValues: defaults(currency) });
  const selectedCurrency = useWatch({ control: form.control, name: "currency" });
  const mutation = useMutation({
    mutationFn: (values: OpenAccountValues) => openAccount(bookID, values),
    onSuccess: async (result) => {
      form.reset(defaults(currency));
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      onOpened(result.account);
    },
  });

  return (
    <form className="open-account-form" onSubmit={form.handleSubmit((values) => mutation.mutate(values))} aria-label="Open account">
      <label>Name<input {...form.register("name")} placeholder="TBC GEL, Wallet, Revolut…" autoFocus /></label>
      <label>Reference <small>IBAN or account number, optional</small>
        <input {...form.register("reference")} placeholder="GE27TB7654321000000003" />
      </label>
      <div className="field-grid compact-fields">
        <div className="field">
          <span className="field-label" id="open-account-currency">Currency</span>
          <Select
            labelledBy="open-account-currency" value={form.watch("currency")}
            onChange={(value) => form.setValue("currency", value as typeof currencies[number], { shouldValidate: true })}
            options={currencies.map((value) => ({ value, label: value, hint: currencyName(value) }))}
          />
        </div>
      </div>
      <label>Balance already on this account<span className="amount-input"><input {...form.register("openingAmount")} inputMode="decimal" placeholder="0.00" /><span>{selectedCurrency}</span></span></label>
      <p className="panel-note">The opening balance is money you already have on the chosen date. It is recorded as an opening entry, not as income, so it never counts against a budget.</p>
      <DateTimeField label="Balance is true as of" value={form.watch("effectiveAt") ?? ""}
        onChange={(value) => form.setValue("effectiveAt", value, { shouldValidate: true })} />
      {selectedCurrency !== currency ? <label>Manual rate<input {...form.register("manualRate")} inputMode="decimal" placeholder={`1 ${selectedCurrency} in ${currency}`} /></label> : null}
      <p className="form-error" role="alert">{Object.values(form.formState.errors)[0]?.message ?? (mutation.error ? humanError(mutation.error) : "")}</p>
      <div className="form-actions">
        <button className="primary-button" type="submit" disabled={mutation.isPending}><span>{mutation.isPending ? "Opening…" : "Open account"}</span><span aria-hidden="true">→</span></button>
        {onCancel ? <button className="secondary-button" type="button" onClick={onCancel}>Cancel</button> : null}
      </div>
    </form>
  );
}
